"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";
import { isCancelledError, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "@/stores/user-store";
import { isAuthError } from "@/lib/query-auth-error";
import { emitPlaceTelemetryEvent, installPlaceErrorCapture, reportPlaceTelemetryError, setPlaceTelemetryUserId } from "@/lib/telemetry/place-telemetry";
import { QueryTimingAccumulator, toQueryOpName } from "@/lib/telemetry/query-timing";

/** 한 화면에 오래 머무를 때(폴링 등)도 이 간격으로는 내보낸다. */
const QUERY_TIMING_FLUSH_INTERVAL_MS = 60_000;

/** FID 는 INP 로 대체된 지표라 보내지 않는다. */
const WEB_VITAL_NAMES = new Set(["TTFB", "FCP", "LCP", "CLS", "INP"]);
interface WebVitalMetric {
  name: string;
  value: number;
  rating?: string;
  navigationType?: string;
}
interface PendingFetch {
  op: string;
  pathname: string;
  startedAt: number;
  cold: boolean;
}

/**
 * 웹 바이탈은 하드 로드(문서) 1번에 대한 값이다. LCP·INP·CLS 는 사용자가 다른
 * 화면으로 이동한 뒤에 확정되는 경우가 많으므로, 보고 시점 경로가 아니라 문서가
 * 처음 열린 경로에 붙인다.
 */
function getLandingPathname(): string {
  try {
    const navigation = performance.getEntriesByType("navigation")[0];
    if (navigation?.name) return new URL(navigation.name).pathname;
  } catch {
    // 구형 브라우저 — 현재 경로로 대체
  }
  return window.location.pathname;
}

// useReportWebVitals 는 콜백 참조가 바뀌면 지금까지의 지표를 다시 보고한다 — 모듈 상수로 고정.
function reportWebVital(metric: WebVitalMetric): void {
  if (!WEB_VITAL_NAMES.has(metric.name)) return;
  emitPlaceTelemetryEvent("custom", {
    kind: "web_vital",
    metric: metric.name,
    // CLS 는 단위 없는 점수(0.1 = 좋음 기준), 나머지는 ms
    value: metric.name === "CLS" ? Math.round(metric.value * 10_000) / 10_000 : Math.round(metric.value),
    rating: metric.rating ?? null,
    navigationType: metric.navigationType ?? null
  }, {
    pathname: getLandingPathname()
  });
}
function flushQueryTimings(accumulator: QueryTimingAccumulator): void {
  for (const batch of accumulator.drain()) {
    emitPlaceTelemetryEvent("custom", {
      kind: "query_timing",
      windowMs: batch.windowMs,
      ops: batch.ops
    }, {
      pathname: batch.pathname
    });
  }
}

/**
 * 서비스별 지표 대시보드용 수집기. 화면에는 아무것도 그리지 않는다.
 *
 * - 사용 속도: 웹 바이탈(LCP·INP·CLS·FCP·TTFB), 기능별 데이터 로딩 시간(쿼리 키 단위)
 * - 에러: 잡히지 않은 런타임 에러, 쿼리·뮤테이션 최종 실패(재시도 소진 후)
 *
 * TelemetryProvider 와 QueryClientProvider 안쪽에 둔다.
 */
export function TelemetryCollectors() {
  const $ = _c(13);
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const userId = useUserStore(_temp);
  const [accumulator] = useState(_temp2);
  useReportWebVitals(reportWebVital);
  let t0;
  let t1;
  if ($[0] !== userId) {
    t0 = () => {
      setPlaceTelemetryUserId(userId);
    };
    t1 = [userId];
    $[0] = userId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(_temp3, t2);
  let t3;
  let t4;
  if ($[4] !== accumulator || $[5] !== queryClient) {
    t3 = () => {
      const pending = new Map();
      const finish = (queryHash, ok) => {
        const started = pending.get(queryHash);
        if (!started) {
          return;
        }
        pending.delete(queryHash);
        accumulator.record({
          pathname: started.pathname,
          op: started.op,
          durationMs: performance.now() - started.startedAt,
          cold: started.cold,
          ok
        });
      };
      const unsubscribeQueries = queryClient.getQueryCache().subscribe(event => {
        if (event.type === "removed") {
          pending.delete(event.query.queryHash);
          return;
        }
        if (event.type !== "updated") {
          return;
        }
        const {
          query,
          action
        } = event;
        switch (action.type) {
          case "fetch":
            {
              const op = toQueryOpName(query.queryKey);
              if (!op) {
                return;
              }
              pending.set(query.queryHash, {
                op,
                pathname: window.location.pathname,
                startedAt: performance.now(),
                cold: query.state.data === undefined
              });
              return;
            }
          case "success":
            {
              if (!action.manual) {
                finish(query.queryHash, true);
              }
              return;
            }
          case "error":
            {
              if (isCancelledError(action.error)) {
                pending.delete(query.queryHash);
                return;
              }
              finish(query.queryHash, false);
              if (!isAuthError(action.error)) {
                reportPlaceTelemetryError(action.error, {
                  source: "query",
                  handled: true,
                  op: toQueryOpName(query.queryKey)
                });
              }
              return;
            }
          case "pause":
            {
              pending.delete(query.queryHash);
              return;
            }
          case "setState":
            {
              if (query.state.fetchStatus === "idle") {
                pending.delete(query.queryHash);
              }
              return;
            }
        }
      });
      const unsubscribeMutations = queryClient.getMutationCache().subscribe(_temp4);
      const flush = () => flushQueryTimings(accumulator);
      const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          flush();
        }
      };
      const interval = window.setInterval(flush, QUERY_TIMING_FLUSH_INTERVAL_MS);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("pagehide", flush);
      return () => {
        unsubscribeQueries();
        unsubscribeMutations();
        window.clearInterval(interval);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("pagehide", flush);
        flush();
      };
    };
    t4 = [queryClient, accumulator];
    $[4] = accumulator;
    $[5] = queryClient;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== accumulator) {
    t5 = () => () => flushQueryTimings(accumulator);
    $[8] = accumulator;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== accumulator || $[11] !== pathname) {
    t6 = [accumulator, pathname];
    $[10] = accumulator;
    $[11] = pathname;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  return null;
}
function _temp4(event_0) {
  if (event_0.type !== "updated" || event_0.action.type !== "error") {
    return;
  }
  const error = event_0.action.error;
  if (isAuthError(error)) {
    return;
  }
  const mutationKey = event_0.mutation.options.mutationKey;
  reportPlaceTelemetryError(error, {
    source: "mutation",
    handled: true,
    op: mutationKey ? toQueryOpName(mutationKey) : null
  });
}
function _temp3() {
  return installPlaceErrorCapture();
}
function _temp2() {
  return new QueryTimingAccumulator();
}
function _temp(state) {
  return state.user?.id ?? null;
}